import {
	Grid,
	FormLabel,
	styled,
	Radio,
	RadioGroup,
	FormControlLabel,
	FormHelperText,
} from "@mui/material";
import { types } from "api";
import { InfoSnackBar } from "components/InfoSnackBar";
import { StyledButton } from "components/StyledButton";
import {
	topics,
	DisplayTopics,
} from "components/FivePointScaleSelector/Topics";
import { ACTIVITIES_FETCH_ERR } from "messages";
import { useEffect, useState } from "react";
import { allSelected } from "./EvaluationHelpers";
import { ReactComponent as BlankIcon } from "assets/icons/sentiment/blank.svg";
import { ReactComponent as NeutralIcon } from "assets/icons/sentiment/neutral.svg";
import { ReactComponent as GoodIcon } from "assets/icons/sentiment/good.svg";
import { ReactComponent as SatisfiedIcon } from "assets/icons/sentiment/satisfied.svg";
import { ReactComponent as VerySatisfiedIcon } from "assets/icons/sentiment/very_satisfied.svg";
const PREFIX = "AddStudentEvaluationPage";

const classes = {
	groups: `${PREFIX}-groups`,
	title: `${PREFIX}-title`,
	button: `${PREFIX}-button`,
	form: `${PREFIX}-form`,
	topicLabel: `${PREFIX}-topicLabel`,
	radio: `${PREFIX}-radio`,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Root = styled("form")(({ theme }) => ({
	[`&.${classes.form}`]: {
		flexGrow: 1,
		padding: 20,
	},

	[`& .${classes.button}`]: {
		marginRight: 5,
		float: "right",
	},

	[`& .${classes.title}`]: {
		marginTop: 5,
		marginBottom: 15,
		float: "left",
	},

	[`& .${classes.radio}`]: {
		marginLeft: theme.spacing(4),
	},
}));

export interface TopicEvaluationProps {
	userIdParam?: string;
	topicGrade: types.GradeById[];
	setTopicGrade: (topicGrade: types.GradeById[]) => void;
	hasTopic: boolean;
	onSubmit: (event: React.FormEvent) => void;
}

export const TopicEvaluation = ({
	topicGrade,
	setTopicGrade,
	hasTopic,
	onSubmit,
}: TopicEvaluationProps): JSX.Element => {
	const [err, setErr] = useState(false);
	const [loaded, setLoaded] = useState(false);
	const [graded, setGraded] = useState(0);

	useEffect(() => {
		if (hasTopic && !loaded) {
			topicGrade.forEach(function (eg) {
				const topic = topics.find((t) => {
					return eg.id === t.id.toString();
				});
				if (topic) {
					topic.grade = +eg.grade;
				}
			});
			setGraded(topicGrade.length);
			setLoaded(true);
		} else if (!hasTopic) {
			topics.forEach(function (topic) {
				topic.grade = 0;
			});
		}
	}, [hasTopic, loaded, topicGrade]);

	const handleChange =
		(field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
			const eg = topicGrade.find((g) => {
				return g.id === field;
			});
			if (eg) {
				eg.grade = event.target.value;
			} else {
				const newGrade: types.GradeById = {
					id: field,
					grade: event.target.value,
				};
				topicGrade.push(newGrade);
			}
			setTopicGrade(topicGrade);
			setGraded(topicGrade.length);
		};

	return (
		<Root className={classes.form} onSubmit={onSubmit}>
			{!allSelected(graded, topics.length) && (
				<FormHelperText>Please add all the evaluations</FormHelperText>
			)}

			<Grid container spacing={1}>
				<Grid item xs={2} />
				<Grid
					item
					xs={10}
					container
					direction="row"
					justifyContent="space-between"
					alignItems="flex-start"
				>
					<Grid item xs={2}>
						<BlankIcon height="100" width="100" />
					</Grid>
					<Grid item xs={2}>
						<NeutralIcon height="100" width="100" />
					</Grid>
					<Grid item xs={2}>
						<SatisfiedIcon height="100" width="100" />
					</Grid>
					<Grid item xs={2}>
						<GoodIcon height="100" width="100" />
					</Grid>
					<Grid item xs={2}>
						<VerySatisfiedIcon height="100" width="100" />
					</Grid>
					<Grid item xs={2}>
						<StyledButton
							variant="contained"
							color="primary"
							className={classes.button}
							type="submit"
							data-testid="submit-edit-topic"
							disabled={!allSelected(graded, topics.length)}
						>
							Submit Edit
						</StyledButton>
					</Grid>
				</Grid>

				{topics.map((topic: DisplayTopics) => (
					<Grid item xs={12} key={`${topic.name}-row`}>
						<Grid
							container
							direction="row"
							justifyContent="space-between"
							alignItems="center"
						>
							<Grid item xs={2} key={`${topic.name}-label`}>
								<FormLabel component="legend">{topic.name}</FormLabel>
							</Grid>
							<Grid
								item
								xs={10}
								key={`${topic.name}-raidoGroup`}
								justifyContent="space-between"
							>
								<RadioGroup
									name={`${topic.name}-${topic.name}-group`}
									aria-label={`${topic.name}-${topic.name}-group`}
									row
									defaultValue={topic.grade ? topic.grade : 0}
									onChange={handleChange(topic.id.toString())}
								>
									{[1, 2, 3, 4, 5].map((value) => (
										<Grid item xs={2} key={`${topic.name}-${value}-raido`}>
											<FormControlLabel
												key={value}
												value={value.toString()}
												className={classes.radio}
												control={<Radio />}
												label=""
											/>
										</Grid>
									))}
								</RadioGroup>
							</Grid>
						</Grid>
					</Grid>
				))}
			</Grid>
			<InfoSnackBar
				isOpen={err}
				onClose={() => setErr(false)}
				message={ACTIVITIES_FETCH_ERR}
				severity="error"
			/>
		</Root>
	);
};
